import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type TicketSummaryContextType = {
  selectedTickets: string[];
  setSelectedTickets: (tickets: string[] | ((prevSelected: string[]) => string[])) => void;
  clearSelectedTickets: () => void;
  isCreatingSummary: boolean;
  setIsCreatingSummary: (value: boolean) => void;
  isSummaryModalOpen: boolean;
  setIsSummaryModalOpen: (value: boolean) => void;
};

const TicketSummaryContext = createContext<TicketSummaryContextType | undefined>(undefined);

export function TicketSummaryProvider({ children }: { children: ReactNode }) {
  // global ticket selection state
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [isCreatingSummary, setIsCreatingSummary] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  const clearSelectedTickets = useCallback(() => {
    setSelectedTickets([]);
  }, []);

  const value = useMemo(
    () => ({
      selectedTickets,
      setSelectedTickets,
      clearSelectedTickets,
      isCreatingSummary,
      setIsCreatingSummary,
      isSummaryModalOpen,
      setIsSummaryModalOpen,
    }),
    [selectedTickets, clearSelectedTickets, isCreatingSummary, isSummaryModalOpen]
  );

  return (
    <TicketSummaryContext.Provider value={value}>
      {children}
    </TicketSummaryContext.Provider>
  );
}

export function useTicketSummary() {
  const context = useContext(TicketSummaryContext);
  if (context === undefined) {
    throw new Error('useTicketSummary must be used within a TicketSummaryProvider');
  }
  return context;
}
